import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocale } from "../context/LocaleContext";

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const { locale } = useLocale();

  useEffect(() => {
    // Extract headings from markdown content
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const matches = Array.from(content.matchAll(headingRegex));

    const extractedHeadings = matches.map((match) => {
      const level = match[1].length;
      const text = match[2].trim();
      // Use the same slug generation as rehype-slug (GitHub Slugger algorithm)
      const id = text
        .toLowerCase()
        .trim()
        .replace(/[\s]+/g, "-") // Replace spaces with hyphens
        .replace(/[^\w\u00C0-\u024F\u1E00-\u1EFF-]/g, "") // Keep word chars, accented chars, and hyphens
        .replace(/--+/g, "-") // Replace multiple hyphens with single hyphen
        .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

      return { id, text, level };
    });

    setHeadings(extractedHeadings);
  }, [content]);

  useEffect(() => {
    // Track which heading is currently visible
    const handleScroll = () => {
      const headingElements = headings.map((h) =>
        document.getElementById(h.id)
      );

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveId(headings[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener("scroll", handleScroll);
  }, [headings]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <motion.nav
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 }}
      className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-4"
    >
      <h3 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-4">
        {locale === "tr-TR" ? "Bu Sayfada" : "On This Page"}
      </h3>
      <ul className="space-y-2 border-l border-gray-800">
        {headings.map((heading) => (
          <li
            key={heading.id}
            style={{
              paddingLeft: `${(heading.level - 1) * 0.75}rem`,
            }}
          >
            <button
              onClick={() => handleClick(heading.id)}
              className={`text-left text-sm transition-colors hover:text-red-600 pl-4 py-1 border-l-2 -ml-px ${
                activeId === heading.id
                  ? "border-red-600 text-red-600 font-medium"
                  : "border-transparent text-gray-400"
              }`}
            >
              {heading.text}
            </button>
          </li>
        ))}
      </ul>
    </motion.nav>
  );
}
